/**
 * NYMPHÉ — First-touch UTM persistence (session-level)
 *
 * Captures `utm_source` / `utm_campaign` / `utm_medium` from the URL on the
 * very first page of a session and stashes them in sessionStorage so creator
 * attribution is retained even if the user lands on `/`, `/the-app`, `/safety`
 * etc. and only later navigates to `/access`.
 *
 * Strict rules:
 *   - First-touch only: never overwrites values already in sessionStorage.
 *   - No PII. Values trimmed, capped at 120 chars.
 *   - No UI effect. No fallback to localStorage.
 */

const KEYS = {
  utm_source:   'nymphe_utm_source',
  utm_campaign: 'nymphe_utm_campaign',
  utm_medium:   'nymphe_utm_medium',
};

const MAX_LEN = 120;

const _clean = (raw) => {
  if (typeof raw !== 'string') return '';
  return raw.trim().slice(0, MAX_LEN);
};

/**
 * Read UTM params from the current URL and persist any non-empty values to
 * sessionStorage — but only if that key is not already present (first-touch).
 * Safe to call on every mount; subsequent calls are no-ops.
 */
export const captureFirstTouchUtm = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    const params = new URLSearchParams(window.location.search);
    Object.entries(KEYS).forEach(([param, key]) => {
      const existing = sessionStorage.getItem(key);
      if (existing) return;                    // first-touch — never overwrite
      const value = _clean(params.get(param));
      if (value) sessionStorage.setItem(key, value);
    });
  } catch {
    /* silent — tracking must never break the page */
  }
};

/**
 * Read the first-touch UTM triple for the current session.
 * Preference order:
 *   1. sessionStorage (persisted first-touch)
 *   2. current URL query string (defensive fallback)
 * Missing values return ''.
 */
export const getFirstTouchUtm = () => {
  const out = { utm_source: '', utm_campaign: '', utm_medium: '' };
  if (typeof window === 'undefined') return out;
  try {
    const params = new URLSearchParams(window.location.search);
    Object.entries(KEYS).forEach(([param, key]) => {
      const fromSession = window.sessionStorage?.getItem(key) || '';
      out[param] = fromSession || _clean(params.get(param));
    });
  } catch {
    /* silent */
  }
  return out;
};
